import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Code2, FlaskConical, Database, Globe, CheckCircle, Clock, BarChart3, TrendingUp } from 'lucide-react';

const MC = { GET:'#00b96b', POST:'#5865f2', PUT:'#f59e0b', PATCH:'#8b5cf6', DELETE:'#ef4444', HEAD:'#6b7280' };

export default function StatsPanel({ projectId, project }) {
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [projectId]);

  async function load() {
    setLoading(true);
    try {
      const [sr, rr] = await Promise.all([
        api.get(`/projects/${projectId}/stats`),
        api.get(`/projects/${projectId}/test-runs?limit=10`)
      ]);
      setStats(sr.data);
      setRuns(rr.data);
    } catch {}
    finally { setLoading(false); }
  }

  if (loading) return <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-3)', fontSize:13 }}>Loading...</div>;
  if (!stats) return null;

  const rateColor = stats.success_rate === null ? 'var(--text-3)' : stats.success_rate >= 80 ? '#16a34a' : stats.success_rate >= 50 ? '#ea580c' : '#dc2626';

  const statCards = [
    { icon:<Code2 size={18}/>,     label:'Endpoints',     val:stats.total_endpoints, color:'#5865f2', bg:'#eff1ff' },
    { icon:<FlaskConical size={18}/>, label:'Total Runs',  val:stats.total_runs,      color:'#7c3aed', bg:'#f5f3ff' },
    { icon:<CheckCircle size={18}/>, label:'Success Rate', val:stats.success_rate!==null?`${stats.success_rate}%`:'—', color:rateColor, bg:'#f0fdf4' },
    { icon:<Clock size={18}/>,    label:'Avg Response',    val:stats.avg_response_time?`${stats.avg_response_time}ms`:'—', color:'#ea580c', bg:'#fff7ed' },
    { icon:<Database size={18}/>, label:'Schemas',         val:stats.total_schemas,   color:'#16a34a', bg:'#f0fdf4' },
    { icon:<Globe size={18}/>,    label:'Environments',    val:stats.total_environments, color:'#2563eb', bg:'#eff6ff' },
  ];

  return (
    <div style={{ flex:1, overflow:'auto', padding:24, background:'var(--bg-secondary)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
        <div style={{ width:40, height:40, borderRadius:10, background:'linear-gradient(135deg,rgba(88,101,242,.12),rgba(124,58,237,.12))', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Code2 size={20} style={{ color:'var(--accent)' }}/>
        </div>
        <div>
          <h2 style={{ fontSize:18, fontWeight:800 }}>{project?.name}</h2>
          {project?.base_url && <code style={{ fontSize:11, color:'var(--text-3)' }}>{project.base_url}</code>}
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))', gap:12, marginBottom:22 }}>
        {statCards.map(s => (
          <div key={s.label} style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px', display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:38, height:38, borderRadius:10, background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', color:s.color, flexShrink:0 }}>{s.icon}</div>
            <div>
              <div style={{ fontSize:24, fontWeight:800, lineHeight:1, color:s.color }}>{s.val ?? 0}</div>
              <div style={{ fontSize:11, color:'var(--text-3)', marginTop:3 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Methods breakdown */}
        <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:12, padding:18 }}>
          <div style={{ display:'flex', alignItems:'center', gap:7, fontWeight:700, fontSize:13, marginBottom:16, color:'var(--text-2)' }}>
            <BarChart3 size={14}/> Endpoints by Method
          </div>
          {(stats.method_counts||[]).map(m => {
            const pct = stats.total_endpoints > 0 ? Math.round((m.count/stats.total_endpoints)*100) : 0;
            return (
              <div key={m.method} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                <span style={{ fontFamily:'var(--mono)', fontWeight:700, fontSize:10, color:MC[m.method]||'#6b7280', width:52 }}>{m.method}</span>
                <div style={{ flex:1, height:7, background:'var(--bg-secondary)', borderRadius:99, overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${pct}%`, background:MC[m.method]||'#6b7280', borderRadius:99, transition:'width .5s ease', opacity:.85 }}/>
                </div>
                <span style={{ fontSize:12, color:'var(--text-2)', width:18, textAlign:'right' }}>{m.count}</span>
              </div>
            );
          })}
          {!stats.method_counts?.length && <div style={{ fontSize:12, color:'var(--text-3)' }}>No endpoints yet</div>}
        </div>

        {/* Recent runs */}
        <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:12, padding:18 }}>
          <div style={{ display:'flex', alignItems:'center', gap:7, fontWeight:700, fontSize:13, marginBottom:16, color:'var(--text-2)' }}>
            <TrendingUp size={14}/> Recent Test Runs
          </div>
          {!runs.length && <div style={{ fontSize:12, color:'var(--text-3)' }}>No test runs yet</div>}
          {runs.slice(0,8).map((run,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
              <span style={{ fontFamily:'var(--mono)', fontWeight:700, fontSize:9, color:MC[run.method]||'#6b7280', width:40 }}>{run.method}</span>
              <span style={{ flex:1, fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{run.endpoint_name||'Request'}</span>
              <span style={{ fontFamily:'var(--mono)', fontWeight:700, fontSize:11, color:run.response_status<300?'#16a34a':run.response_status<500?'#ea580c':'#dc2626' }}>{run.response_status}</span>
              <span style={{ fontSize:10, color:'var(--text-3)', width:42, textAlign:'right' }}>{run.response_time}ms</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
